import { useMemo, useState, useCallback } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import React from "react";

export type SortDirection = "asc" | "desc" | null;

export interface FilterState {
  searchText: string;
  filterTemplateType: string;
  filterFormatType: string;
  filterStatus: string;
}

export interface SortState {
  sortColumn: string | null;
  sortDir: SortDirection;
}

interface LookupType {
  id: number;
  name: string;
}

interface FilterSortOptions<T> {
  items: T[];
  templateTypes: LookupType[];
  formatTypes: LookupType[];
  /** Function to extract the text-searchable fields from an item */
  getSearchableText: (item: T) => string[];
  /** Function to get template_type_id from an item */
  getTemplateTypeId: (item: T) => number | null | undefined;
  /** Function to get template_formatetype_ids from an item */
  getFormatTypeIds: (item: T) => number[] | null | undefined;
  /** Function to get is_active from an item */
  getIsActive: (item: T) => boolean;
  /** Custom sort comparator per column (optional). Receives precomputed lookup maps. */
  sortComparators?: Record<
    string,
    (a: T, b: T, templateTypeMap: Map<number, string>, formatTypeMap: Map<number, string>) => number
  >;
  /** Default sort when no column is selected */
  defaultSort?: (a: T, b: T) => number;
}

export function useFilterSort<T>(options: FilterSortOptions<T>) {
  const {
    items,
    templateTypes,
    formatTypes,
    getSearchableText,
    getTemplateTypeId,
    getFormatTypeIds,
    getIsActive,
    sortComparators = {},
    defaultSort,
  } = options;

  const [sortState, setSortState] = useState<{ column: string | null; dir: SortDirection }>({
    column: null,
    dir: null,
  });
  const sortColumn = sortState.column;
  const sortDir = sortState.dir;
  const [searchText, setSearchText] = useState("");
  const [filterTemplateType, setFilterTemplateType] = useState<string>("all");
  const [filterFormatType, setFilterFormatType] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const cycleSort = useCallback((column: string) => {
    setSortState((prev) => {
      if (prev.column !== column) {
        // New column selected: reset direction to asc
        return { column, dir: "asc" };
      }
      // Same column: cycle asc -> desc -> null
      if (prev.dir === "asc") return { column, dir: "desc" };
      if (prev.dir === "desc") return { column: null, dir: null };
      return { column, dir: "asc" };
    });
  }, []);

  const getSortIcon = useCallback(
    (column: string) => {
      if (sortColumn !== column)
        return React.createElement(ArrowUpDown, { className: "w-3.5 h-3.5 text-muted-foreground" });
      if (sortDir === "asc")
        return React.createElement(ArrowUp, { className: "w-3.5 h-3.5 text-muted-foreground" });
      return React.createElement(ArrowDown, { className: "w-3.5 h-3.5 text-muted-foreground" });
    },
    [sortColumn, sortDir]
  );

  // Precompute id → name lookup maps (addresses perf review comment)
  const templateTypeMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const t of templateTypes) {
      map.set(t.id, t.name);
    }
    return map;
  }, [templateTypes]);

  const formatTypeMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const f of formatTypes) {
      map.set(f.id, f.name);
    }
    return map;
  }, [formatTypes]);

  const filteredItems = useMemo(() => {
    let result = items;
    const trimmed = searchText.trim();
    if (trimmed) {
      const q = trimmed.toLowerCase();
      result = result.filter((item) =>
        getSearchableText(item).some((text) => text.toLowerCase().includes(q))
      );
    }
    if (filterTemplateType !== "all") {
      result = result.filter((item) => String(getTemplateTypeId(item)) === filterTemplateType);
    }
    if (filterFormatType !== "all") {
      result = result.filter((item) =>
        getFormatTypeIds(item)?.includes(Number(filterFormatType))
      );
    }
    if (filterStatus !== "all") {
      const isActive = filterStatus === "active";
      result = result.filter((item) => getIsActive(item) === isActive);
    }
    return result;
  }, [items, searchText, filterTemplateType, filterFormatType, filterStatus, getSearchableText, getTemplateTypeId, getFormatTypeIds, getIsActive]);

  const sortedItems = useMemo(() => {
    const arr = [...filteredItems];
    if (!sortColumn || !sortDir) {
      return defaultSort ? arr.sort(defaultSort) : arr;
    }

    // Check for custom comparator first
    const customCmp = sortComparators[sortColumn];
    if (customCmp) {
      return arr.sort((a, b) => {
        const cmp = customCmp(a, b, templateTypeMap, formatTypeMap);
        return sortDir === "asc" ? cmp : -cmp;
      });
    }

    // Built-in comparators
    return arr.sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case "name":
          cmp = ((a as any).name ?? "").localeCompare((b as any).name ?? "");
          break;
        case "templateType": {
          const nameA = templateTypeMap.get(getTemplateTypeId(a) ?? -1) ?? "";
          const nameB = templateTypeMap.get(getTemplateTypeId(b) ?? -1) ?? "";
          cmp = nameA.localeCompare(nameB);
          break;
        }
        case "formatTypes": {
          const fmtA = (getFormatTypeIds(a) ?? []).map((id) => formatTypeMap.get(id) ?? "").join(", ");
          const fmtB = (getFormatTypeIds(b) ?? []).map((id) => formatTypeMap.get(id) ?? "").join(", ");
          cmp = fmtA.localeCompare(fmtB);
          break;
        }
        case "status":
          cmp = getIsActive(a) === getIsActive(b) ? 0 : getIsActive(a) ? -1 : 1;
          break;
        default:
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredItems, sortColumn, sortDir, templateTypeMap, formatTypeMap, sortComparators, defaultSort, getTemplateTypeId, getFormatTypeIds, getIsActive]);

  return {
    // Sort
    sortColumn,
    sortDir,
    cycleSort,
    getSortIcon,
    // Filter
    searchText,
    setSearchText,
    filterTemplateType,
    setFilterTemplateType,
    filterFormatType,
    setFilterFormatType,
    filterStatus,
    setFilterStatus,
    // Results
    filteredItems,
    sortedItems,
    // Lookup maps (useful for rendering)
    templateTypeMap,
    formatTypeMap,
  };
}
